import { API_CONFIG } from './config';

/**
 * The single origin this app is allowed to send requests to.
 *
 * `API_CONFIG.baseURL` comes from `NEXT_PUBLIC_API_URL`, which is build-time
 * configuration rather than a literal — so every `fetch` built from it is, as
 * far as a static analyser can prove, a request to an arbitrary host (CWE-918).
 * Pinning the origin once here and asserting it at every call site turns that
 * into a checked invariant: a path that would leave the API origin throws
 * instead of being fetched.
 */
const API_ORIGIN = (() => {
  try {
    return new URL(API_CONFIG.baseURL).origin;
  } catch {
    // A malformed NEXT_PUBLIC_API_URL must not take the whole bundle down at
    // import time — `resolveApiUrl` will reject every request instead.
    return null;
  }
})();

/**
 * Resolve an API path to an absolute URL and prove it still points at the
 * configured API origin.
 *
 * Concatenation (rather than `new URL(path, base)`) is deliberate: it preserves
 * any path prefix carried by `NEXT_PUBLIC_API_URL`, which a root-relative
 * `path` would otherwise discard. The origin assertion is what makes it safe —
 * an absolute URL, a protocol-relative `//host`, or a `@host` userinfo trick
 * spliced into `path` all resolve to a different origin and are rejected here,
 * before any network call happens.
 *
 * @param path Root-relative API path, optionally with a query string.
 * @throws If the resolved URL is not on the configured API origin.
 */
export function resolveApiUrl(path: string): string {
  if (!API_ORIGIN) {
    throw new Error('API base URL is not configured');
  }

  let resolved: URL;
  try {
    resolved = new URL(`${API_CONFIG.baseURL}${path}`);
  } catch {
    throw new Error('Refusing to request a malformed API URL');
  }

  if (resolved.origin !== API_ORIGIN) {
    throw new Error(
      `Refusing to request a non-API origin: ${resolved.origin} (expected ${API_ORIGIN})`
    );
  }

  return resolved.toString();
}
